import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";

// OTP icons
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

import { useAuth } from "../AuthContext";

import InquiryAPI from "../../../../api/Inquiry";
import { wpFetchMe, wpSetCachedUser } from "../../../../api/wpAuth";

import {
  PASSWORD_RULE_ITEMS,
  evaluatePasswordRules,
  fonts,
  formatMMSS,
  getPasswordStrength,
  gqlChangeMyPassword,
  gqlFetchMyProfile,
  gqlUpdateMyProfile,
  initialPasswordRules,
  normalizeLang,
  normalizeWpUser,
  sanitizeOtpInput,
  tForLang,
} from "../AccountUtils";

import {
  AuthErrorMessage,
  extractWpErrorMessage,
  mapWpErrorToUiMessage,
} from "../../../GlobalFunction/AuthErrorMessage";

/* -------------------------- Basic Info Form ------------------------ */
function BasicInfoForm({ user, lang, onUserUpdated }) {
  const t = (key) => tForLang(lang, key);
  const { requestEmailOtp, verifyEmailOtp } = useAuth();

  const [form, setForm] = useState({
    username: user?.username || "",
    lastName: user?.lastName || "",
    firstName: user?.firstName || "",
    birthday: user?.birthday || "",
    gender: user?.gender || "male",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    landlinePhoneNumber: user?.landlinePhoneNumber || "",
    address: user?.address || "",
  });

  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // view-only by default; unlock editing only after OTP verified
  const [editing, setEditing] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);

  // email change flow
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [emailOtpDialogOpen, setEmailOtpDialogOpen] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [emailOtpVerifying, setEmailOtpVerifying] = useState(false);
  const [emailOtpExpiresAtMs, setEmailOtpExpiresAtMs] = useState(0);
  const [emailOtpResendAvailableAtMs, setEmailOtpResendAvailableAtMs] =
    useState(0);
  const [emailOtpStatusMsg, setEmailOtpStatusMsg] = useState("");
  const [emailOtpVerifiedEmail, setEmailOtpVerifiedEmail] = useState("");

  // OTP for unlocking edit_profile
  const [editOtp, setEditOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpExpiresAtMs, setOtpExpiresAtMs] = useState(0);
  const [otpResendAvailableAtMs, setOtpResendAvailableAtMs] = useState(0);
  const [otpStatusMsg, setOtpStatusMsg] = useState("");
  const [editOtpVerified, setEditOtpVerified] = useState(false);

  const userEmail = String(user?.email || "").trim();

  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const it = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(it);
  }, []);

  useEffect(() => {
    setForm({
      username: user?.username || "",
      lastName: user?.lastName || "",
      firstName: user?.firstName || "",
      birthday: user?.birthday || "",
      gender: user?.gender || "male",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || "",
      landlinePhoneNumber: user?.landlinePhoneNumber || "",
      address: user?.address || "",
    });

    setErr("");
    setOk(false);

    setEditing(false);
    setOtpDialogOpen(false);

    setEditOtp("");
    setOtpStatusMsg("");
    setOtpExpiresAtMs(0);
    setOtpResendAvailableAtMs(0);
    setEditOtpVerified(false);

    setEmailUnlocked(false);
    setEmailOtpDialogOpen(false);
    setEmailOtp("");
    setEmailOtpStatusMsg("");
    setEmailOtpExpiresAtMs(0);
    setEmailOtpResendAvailableAtMs(0);
    setEmailOtpVerifiedEmail("");
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setForm((s) => ({
      ...s,
      email: user?.email || "",
    }));
  }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (k) => (e) => {
    const v = e.target.value;

    if (k === "email") {
      const next = String(v || "").trim();
      const prev = String(form.email || "").trim();

      setForm((s) => ({ ...s, email: v }));

      if (next.toLowerCase() !== prev.toLowerCase()) {
        setEmailOtpVerifiedEmail("");
        setEmailOtp("");
        setEmailOtpStatusMsg("");
        setEmailOtpExpiresAtMs(0);
        setEmailOtpResendAvailableAtMs(0);
      }
      return;
    }

    setForm((s) => ({ ...s, [k]: v }));
  };

  const otpExpired =
    !editOtpVerified && otpExpiresAtMs > 0 && nowTick >= otpExpiresAtMs;

  const otpRemainingSec =
    !editOtpVerified && otpExpiresAtMs
      ? Math.max(0, Math.ceil((otpExpiresAtMs - nowTick) / 1000))
      : 0;

  const resendCooldownSec =
    !editOtpVerified && otpResendAvailableAtMs
      ? Math.max(0, Math.ceil((otpResendAvailableAtMs - nowTick) / 1000))
      : 0;

  const canSendOtp = !editOtpVerified && !otpSending && resendCooldownSec <= 0;
  const canVerifyOtp = !editOtpVerified && !!editOtp && !otpVerifying;

  const openOtpDialog = () => {
    setErr("");
    setOtpStatusMsg("");
    setEditOtp("");
    setOtpExpiresAtMs(0);
    setOtpResendAvailableAtMs(0);
    setEditOtpVerified(false);
    setOtpDialogOpen(true);
  };

  const closeOtpDialog = () => {
    if (otpSending || otpVerifying) return;
    setOtpDialogOpen(false);
  };

  const handleSendOtp = async () => {
    if (!canSendOtp) return;

    setErr("");
    setOtpStatusMsg("");
    setOtpSending(true);

    try {
      const payload = await requestEmailOtp({
        purpose: "edit_profile",
        email: userEmail || "",
        lang,
      });

      const expiresIn = Number(payload?.expiresIn);
      const ttlSec =
        Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 300;

      const now = Date.now();
      setOtpExpiresAtMs(now + ttlSec * 1000);
      setOtpResendAvailableAtMs(now + 60 * 1000);
      setOtpStatusMsg(t("otpSent"));
      setEditOtpVerified(false);
    } catch (e) {
      const msg = extractWpErrorMessage(e);
      setErr(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "basicUpdateFailed",
        }),
      );
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!editOtp.trim()) return setErr(t("otpRequired"));

    setErr("");
    setOtpStatusMsg("");
    setOtpVerifying(true);

    try {
      await verifyEmailOtp({
        purpose: "edit_profile",
        email: userEmail || "",
        code: String(editOtp || "").trim(),
      });

      setOtpStatusMsg(t("otpVerified"));
      setEditOtpVerified(true);

      setEditing(true);
      setEmailUnlocked(true);
      setOtpDialogOpen(false);

      setOtpExpiresAtMs(0);
      setOtpResendAvailableAtMs(0);
    } catch (e) {
      const msg = extractWpErrorMessage(e);
      setErr(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "basicUpdateFailed",
        }),
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({
      username: user?.username || "",
      lastName: user?.lastName || "",
      firstName: user?.firstName || "",
      birthday: user?.birthday || "",
      gender: user?.gender || "male",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || "",
      landlinePhoneNumber: user?.landlinePhoneNumber || "",
      address: user?.address || "",
    });

    setErr("");
    setOk(false);

    setEditOtp("");
    setOtpStatusMsg("");
    setOtpExpiresAtMs(0);
    setOtpResendAvailableAtMs(0);
    setEditOtpVerified(false);

    setEmailUnlocked(false);
    setEmailOtpDialogOpen(false);
    setEmailOtp("");
    setEmailOtpStatusMsg("");
    setEmailOtpExpiresAtMs(0);
    setEmailOtpResendAvailableAtMs(0);
    setEmailOtpVerifiedEmail("");
  };

  // NEW EMAIL CHANGE OTP (purpose: change_email)
  const currentEmail = String(userEmail || "").trim();
  const draftEmail = String(form.email || "").trim();

  const isChangingEmail =
    !!draftEmail &&
    !!currentEmail &&
    draftEmail.toLowerCase() !== currentEmail.toLowerCase();

  const emailOtpExpired =
    !emailOtpVerifiedEmail &&
    emailOtpExpiresAtMs > 0 &&
    nowTick >= emailOtpExpiresAtMs;

  const emailOtpRemainingSec =
    !emailOtpVerifiedEmail && emailOtpExpiresAtMs
      ? Math.max(0, Math.ceil((emailOtpExpiresAtMs - nowTick) / 1000))
      : 0;

  const emailResendCooldownSec =
    !emailOtpVerifiedEmail && emailOtpResendAvailableAtMs
      ? Math.max(0, Math.ceil((emailOtpResendAvailableAtMs - nowTick) / 1000))
      : 0;

  const canSendEmailOtp =
    editing &&
    emailUnlocked &&
    isChangingEmail &&
    !emailOtpSending &&
    emailResendCooldownSec <= 0 &&
    !emailOtpVerifiedEmail;

  const canVerifyEmailOtp =
    editing &&
    emailUnlocked &&
    isChangingEmail &&
    !emailOtpVerifying &&
    !!String(emailOtp || "").trim() &&
    !emailOtpVerifiedEmail;

  const openEmailOtpDialog = () => {
    if (!editing || !emailUnlocked) return;
    setErr("");
    setEmailOtpStatusMsg("");
    setEmailOtp("");
    setEmailOtpExpiresAtMs(0);
    setEmailOtpResendAvailableAtMs(0);
    setEmailOtpVerifiedEmail("");
    setEmailOtpDialogOpen(true);
  };

  const closeEmailOtpDialog = () => {
    if (emailOtpSending || emailOtpVerifying) return;
    setEmailOtpDialogOpen(false);
  };

  const handleSendEmailChangeOtp = async () => {
    if (!canSendEmailOtp) return;

    setErr("");
    setEmailOtpStatusMsg("");
    setEmailOtpSending(true);

    try {
      const payload = await requestEmailOtp({
        purpose: "change_email",
        email: draftEmail,
        lang,
      });

      const expiresIn = Number(payload?.expiresIn);
      const ttlSec =
        Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 300;

      const now = Date.now();
      setEmailOtpExpiresAtMs(now + ttlSec * 1000);
      setEmailOtpResendAvailableAtMs(now + 60 * 1000);
      setEmailOtpStatusMsg(t("otpSent"));
      setEmailOtpVerifiedEmail("");
    } catch (e) {
      const msg = extractWpErrorMessage(e);
      setErr(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "basicUpdateFailed",
        }),
      );
    } finally {
      setEmailOtpSending(false);
    }
  };

  const handleVerifyEmailChangeOtp = async () => {
    if (!String(emailOtp || "").trim()) return setErr(t("otpRequired"));

    setErr("");
    setEmailOtpStatusMsg("");
    setEmailOtpVerifying(true);

    try {
      await verifyEmailOtp({
        purpose: "change_email",
        email: draftEmail,
        code: String(emailOtp || "").trim(),
      });

      setEmailOtpStatusMsg(t("otpVerified"));
      setEmailOtpVerifiedEmail(draftEmail);
      setEmailOtpDialogOpen(false);

      setEmailOtpExpiresAtMs(0);
      setEmailOtpResendAvailableAtMs(0);
    } catch (e) {
      const msg = extractWpErrorMessage(e);
      setErr(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "basicUpdateFailed",
        }),
      );
    } finally {
      setEmailOtpVerifying(false);
    }
  };

  // SUBMIT
  const submit = async () => {
    setErr("");

    if (!editing) return;

    if (!editOtpVerified) return setErr(t("otpNeedVerifyBeforeSave"));
    if (otpExpired) return setErr(t("otpExpired"));

    if (isChangingEmail) {
      const verifiedOk =
        String(emailOtpVerifiedEmail || "")
          .trim()
          .toLowerCase() ===
        String(draftEmail || "")
          .trim()
          .toLowerCase();
      if (!verifiedOk) return setErr(t("otpNeedVerifyBeforeSave"));
      if (emailOtpExpired) return setErr(t("otpExpired"));
    }

    setSaving(true);
    try {
      const input = {
        email: isChangingEmail ? draftEmail : undefined,
        emailOtp: isChangingEmail
          ? String(emailOtp || "").trim()
          : String(editOtp || "").trim(),
        firstName: form.firstName || "",
        lastName: form.lastName || "",
        phoneNumber: form.phoneNumber || "",
        landlinePhoneNumber:
          form.landlinePhoneNumber !== undefined
            ? String(form.landlinePhoneNumber)
            : "",
        address: form.address !== undefined ? String(form.address) : "",
        birthday: form.birthday || "",
        gender: form.gender || "male",
      };

      const clearableStringFields = new Set(["landlinePhoneNumber", "address"]);

      Object.keys(input).forEach((key) => {
        if (input[key] === undefined) {
          delete input[key];
          return;
        }

        if (
          typeof input[key] === "string" &&
          input[key].trim() === "" &&
          !clearableStringFields.has(key)
        ) {
          delete input[key];
        }
      });

      const data = await gqlUpdateMyProfile(input);
      const payload = data?.sehoUpdateMyProfile;
      if (!payload?.ok) throw new Error(payload?.message || "update failed");

      let next = null;
      try {
        const me2 = await gqlFetchMyProfile();
        next = normalizeWpUser(me2?.viewer);
      } catch {
        const me = await wpFetchMe({ debug: false });
        next = normalizeWpUser(me);
      }

      onUserUpdated?.(next);
      wpSetCachedUser(next);

      setEditing(false);

      setEditOtp("");
      setOtpStatusMsg("");
      setOtpExpiresAtMs(0);
      setOtpResendAvailableAtMs(0);
      setEditOtpVerified(false);

      setEmailUnlocked(false);
      setEmailOtpDialogOpen(false);
      setEmailOtp("");
      setEmailOtpStatusMsg("");
      setEmailOtpExpiresAtMs(0);
      setEmailOtpResendAvailableAtMs(0);
      setEmailOtpVerifiedEmail("");

      setOk(true);
    } catch (e) {
      console.error(e);
      const msg = extractWpErrorMessage(e);
      setErr(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "basicUpdateFailed",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const langCode = normalizeLang(lang);

  const emailVerifiedForNewEmail =
    isChangingEmail &&
    String(emailOtpVerifiedEmail || "")
      .trim()
      .toLowerCase() ===
      String(draftEmail || "")
        .trim()
        .toLowerCase();

  const showEmailVerifyBlock = editing && emailUnlocked && isChangingEmail;

  const saveDisabled =
    !editing ||
    saving ||
    !editOtpVerified ||
    (isChangingEmail && !emailVerifiedForNewEmail);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          my: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            variant="h6"
            sx={{ fontFamily: fonts[lang], fontWeight: 900 }}
          >
            {t("basicTitle")}
          </Typography>

          {!editing ? (
            <Button variant="outlined" onClick={openOtpDialog}>
              {langCode === "ZH" ? "編輯" : langCode === "JP" ? "編集" : "Edit"}
            </Button>
          ) : (
            <Button variant="text" onClick={cancelEdit}>
              {langCode === "ZH"
                ? "取消"
                : langCode === "JP"
                  ? "キャンセル"
                  : "Cancel"}
            </Button>
          )}
        </Stack>

        <Stack spacing={2}>
          <TextField
            label={t("basicUsernameLabel")}
            value={user?.username || ""}
            InputProps={{ readOnly: true }}
            fullWidth
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            <TextField
              label={t("basicPhoneLabel")}
              value={form.phoneNumber}
              onChange={onChange("phoneNumber")}
              fullWidth
              disabled={!editing}
            />

            <TextField
              label={t("basicLandlinePhoneLabel")}
              value={form.landlinePhoneNumber}
              onChange={onChange("landlinePhoneNumber")}
              fullWidth
              disabled={!editing}
              inputProps={{
                inputMode: "tel",
              }}
            />
          </Box>

          <TextField
            label={t("basicAddressLabel")}
            value={form.address}
            onChange={onChange("address")}
            fullWidth
            multiline
            minRows={2}
            disabled={!editing}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label={t("basicLastName")}
              value={form.lastName}
              onChange={onChange("lastName")}
              disabled={!editing}
            />
            <TextField
              fullWidth
              label={t("basicFirstName")}
              value={form.firstName}
              onChange={onChange("firstName")}
              disabled={!editing}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label={t("basicBirthday")}
              type="date"
              value={form.birthday || ""}
              onChange={onChange("birthday")}
              InputLabelProps={{ shrink: true }}
              disabled={!editing}
            />

            <FormControl fullWidth disabled={!editing}>
              <FormLabel
                sx={{ color: "text.secondary", mb: 1, fontFamily: fonts[lang] }}
              >
                {t("basicGender")}
              </FormLabel>
              <RadioGroup
                row
                value={form.gender}
                onChange={onChange("gender")}
                sx={{
                  "& .MuiFormControlLabel-label": { fontFamily: fonts[lang] },
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label={t("genderMale")}
                />
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label={t("genderFemale")}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label={t("basicEmailLabel")}
            value={form.email || ""}
            onChange={onChange("email")}
            disabled={!editing || !emailUnlocked}
            helperText={
              !editing
                ? ""
                : !emailUnlocked
                  ? langCode === "ZH"
                    ? "完成 Email 驗證後才能修改 Email。"
                    : langCode === "JP"
                      ? "メール認証後にメールアドレスを変更できます。"
                      : "Verify email first to edit email address."
                  : isChangingEmail
                    ? langCode === "ZH"
                      ? "變更 Email 後需要先驗證新 Email 的 OTP 才能儲存。"
                      : langCode === "JP"
                        ? "新しいメールで OTP を確認してから保存できます。"
                        : "After changing email, verify OTP for the new email before saving."
                    : ""
            }
          />

          {showEmailVerifyBlock && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: emailVerifiedForNewEmail
                  ? "success.main"
                  : "divider",
              }}
            >
              <Stack spacing={1}>
                <Typography
                  sx={{
                    fontFamily: fonts[lang],
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {langCode === "ZH"
                    ? "新 Email 驗證"
                    : langCode === "JP"
                      ? "新しいメールの認証"
                      : "Verify new email"}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: fonts[lang],
                    fontSize: 12,
                    color: "text.secondary",
                  }}
                >
                  {langCode === "ZH"
                    ? `請先將 OTP 寄送到新 Email：${draftEmail}`
                    : langCode === "JP"
                      ? `新しいメールへ OTP を送信：${draftEmail}`
                      : `Send OTP to the new email: ${draftEmail}`}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={openEmailOtpDialog}
                    disabled={emailOtpSending || emailOtpVerifying}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {langCode === "ZH"
                      ? "驗證新 Email"
                      : langCode === "JP"
                        ? "新メールを認証"
                        : "Verify new email"}
                  </Button>

                  {emailVerifiedForNewEmail && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CheckCircleRoundedIcon
                        sx={{ fontSize: 18, color: "success.main" }}
                      />
                      <Typography sx={{ fontSize: 12, color: "success.main" }}>
                        {langCode === "ZH"
                          ? "已驗證"
                          : langCode === "JP"
                            ? "確認済み"
                            : "Verified"}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {!emailVerifiedForNewEmail && (
                  <Typography
                    sx={{
                      fontFamily: fonts[lang],
                      fontSize: 12,
                      color: "warning.main",
                    }}
                  >
                    {langCode === "ZH"
                      ? "未完成新 Email 驗證，無法儲存。"
                      : langCode === "JP"
                        ? "新しいメールが未認証のため保存できません。"
                        : "New email not verified yet. Save is disabled."}
                  </Typography>
                )}
              </Stack>
            </Paper>
          )}

          <AuthErrorMessage
            text={err}
            sx={{ fontFamily: fonts[lang], fontSize: 13 }}
          />

          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={saveDisabled}
            >
              {t("basicSaveButton")}
            </Button>
          </Stack>

          {!editing && (
            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                fontFamily: fonts[lang],
              }}
            >
              {langCode === "ZH"
                ? "目前為檢視模式。按「編輯」後會寄送驗證碼到您的 Email，驗證後才能修改資料與 Email。"
                : langCode === "JP"
                  ? "現在は閲覧モードです。「編集」を押すとメールに認証コードを送信し、確認後に編集できます。"
                  : "View-only mode. Click Edit to receive an email OTP, then verify to unlock editing."}
            </Typography>
          )}
        </Stack>

        <Snackbar
          open={ok}
          autoHideDuration={2000}
          onClose={() => setOk(false)}
        >
          <Alert severity="success" variant="filled">
            {t("basicUpdatedMessage")}
          </Alert>
        </Snackbar>
      </Paper>

      {/* OTP Dialog: edit_profile */}
      <Dialog
        open={otpDialogOpen}
        onClose={closeOtpDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: fonts[lang], fontWeight: 900 }}>
          {langCode === "ZH"
            ? "安全驗證（Email 驗證碼）"
            : langCode === "JP"
              ? "セキュリティ確認（メール認証）"
              : "Security verification (Email OTP)"}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              fontFamily: fonts[lang],
              mb: 1,
            }}
          >
            {langCode === "ZH"
              ? `驗證碼將寄送至：${userEmail || "（系統將使用帳號 Email）"}`
              : langCode === "JP"
                ? `コード送信先：${userEmail || "（アカウントのメールを使用）"}`
                : `OTP will be sent to: ${userEmail || "(uses your account email)"}`}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <TextField
            fullWidth
            label={t("otpLabel")}
            value={editOtp}
            onChange={(e) => setEditOtp(sanitizeOtpInput(e.target.value))}
            disabled={editOtpVerified}
            helperText={
              otpStatusMsg
                ? otpStatusMsg
                : otpExpired
                  ? t("otpExpired")
                  : t("otpHint")
            }
            FormHelperTextProps={{ sx: { mt: 0.5 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MarkEmailReadRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {!!otpExpiresAtMs && !otpExpired && !editOtpVerified && (
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {formatMMSS(otpRemainingSec)}
                    </Typography>
                  )}
                  {editOtpVerified && (
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 18, color: "success.main" }}
                    />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": editOtpVerified
                ? {
                    "& fieldset": { borderColor: "success.main" },
                    "&:hover fieldset": { borderColor: "success.main" },
                    "&.Mui-focused fieldset": { borderColor: "success.main" },
                  }
                : {},
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleSendOtp}
              disabled={!canSendOtp}
              startIcon={
                otpExpiresAtMs ? <ReplayRoundedIcon /> : <SendRoundedIcon />
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              {otpExpiresAtMs ? t("otpResend") : t("otpSend")}
              {resendCooldownSec > 0 ? ` (${resendCooldownSec}s)` : ""}
            </Button>

            <Button
              type="button"
              variant="text"
              onClick={handleVerifyOtp}
              disabled={!canVerifyOtp}
              sx={{ whiteSpace: "nowrap" }}
            >
              {otpVerifying
                ? "..."
                : langCode === "ZH"
                  ? "驗證"
                  : langCode === "JP"
                    ? "確認"
                    : "Verify"}
            </Button>
          </Stack>

          {!editOtpVerified && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: "text.secondary",
                fontFamily: fonts[lang],
              }}
            >
              {langCode === "ZH"
                ? "完成驗證後才可編輯會員資料與 Email。"
                : langCode === "JP"
                  ? "確認が完了すると編集できます。"
                  : "You can edit your info after OTP verification."}
            </Typography>
          )}

          <AuthErrorMessage
            text={err}
            sx={{ mt: 1, fontFamily: fonts[lang], fontSize: 13 }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeOtpDialog}
            disabled={otpSending || otpVerifying}
          >
            {langCode === "ZH"
              ? "關閉"
              : langCode === "JP"
                ? "閉じる"
                : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change OTP Dialog: change_email */}
      <Dialog
        open={emailOtpDialogOpen}
        onClose={closeEmailOtpDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: fonts[lang], fontWeight: 900 }}>
          {langCode === "ZH"
            ? "新 Email 驗證（OTP）"
            : langCode === "JP"
              ? "新しいメール認証（OTP）"
              : "Verify new email (OTP)"}
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              fontFamily: fonts[lang],
              mb: 1,
            }}
          >
            {langCode === "ZH"
              ? `驗證碼將寄送至：${draftEmail || "-"}`
              : langCode === "JP"
                ? `コード送信先：${draftEmail || "-"}`
                : `OTP will be sent to: ${draftEmail || "-"}`}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <TextField
            fullWidth
            label={t("otpLabel")}
            value={emailOtp}
            onChange={(e) => setEmailOtp(sanitizeOtpInput(e.target.value))}
            disabled={!!emailOtpVerifiedEmail}
            helperText={
              emailOtpStatusMsg
                ? emailOtpStatusMsg
                : emailOtpExpired
                  ? t("otpExpired")
                  : t("otpHint")
            }
            FormHelperTextProps={{ sx: { mt: 0.5 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MarkEmailReadRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {!!emailOtpExpiresAtMs &&
                    !emailOtpExpired &&
                    !emailOtpVerifiedEmail && (
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        {formatMMSS(emailOtpRemainingSec)}
                      </Typography>
                    )}
                  {!!emailOtpVerifiedEmail && (
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 18, color: "success.main" }}
                    />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": emailOtpVerifiedEmail
                ? {
                    "& fieldset": { borderColor: "success.main" },
                    "&:hover fieldset": { borderColor: "success.main" },
                    "&.Mui-focused fieldset": { borderColor: "success.main" },
                  }
                : {},
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleSendEmailChangeOtp}
              disabled={!canSendEmailOtp}
              startIcon={
                emailOtpExpiresAtMs ? (
                  <ReplayRoundedIcon />
                ) : (
                  <SendRoundedIcon />
                )
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              {emailOtpExpiresAtMs ? t("otpResend") : t("otpSend")}
              {emailResendCooldownSec > 0
                ? ` (${emailResendCooldownSec}s)`
                : ""}
            </Button>

            <Button
              type="button"
              variant="text"
              onClick={handleVerifyEmailChangeOtp}
              disabled={!canVerifyEmailOtp}
              sx={{ whiteSpace: "nowrap" }}
            >
              {emailOtpVerifying
                ? "..."
                : langCode === "ZH"
                  ? "驗證"
                  : langCode === "JP"
                    ? "確認"
                    : "Verify"}
            </Button>
          </Stack>

          {!emailOtpVerifiedEmail && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: "text.secondary",
                fontFamily: fonts[lang],
              }}
            >
              {langCode === "ZH"
                ? "完成新 Email 驗證後才可儲存。"
                : langCode === "JP"
                  ? "新しいメールの確認後に保存できます。"
                  : "You can save after verifying the new email."}
            </Typography>
          )}

          <AuthErrorMessage
            text={err}
            sx={{ mt: 1, fontFamily: fonts[lang], fontSize: 13 }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeEmailOtpDialog}
            disabled={emailOtpSending || emailOtpVerifying}
          >
            {langCode === "ZH"
              ? "關閉"
              : langCode === "JP"
                ? "閉じる"
                : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* -------------------------- Change Password ------------------------ */
function PasswordForm({ lang, user }) {
  const t = (key) => tForLang(lang, key);
  const { requestEmailOtp, verifyEmailOtp } = useAuth();

  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({ old: "", next: "" });
  const [showOld, setShowOld] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [passwordRules, setPasswordRules] = useState(initialPasswordRules);
  const [error, setError] = useState("");
  const [confirmWeakOpen, setConfirmWeakOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pwdOtp, setPwdOtp] = useState("");
  const [pwdOtpSending, setPwdOtpSending] = useState(false);
  const [pwdOtpVerifying, setPwdOtpVerifying] = useState(false);
  const [pwdOtpExpiresAtMs, setPwdOtpExpiresAtMs] = useState(0);
  const [pwdOtpResendAvailableAtMs, setPwdOtpResendAvailableAtMs] = useState(0);
  const [pwdOtpStatusMsg, setPwdOtpStatusMsg] = useState("");
  const [pwdOtpVerified, setPwdOtpVerified] = useState(false);

  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const it = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(it);
  }, []);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleNextChange = (e) => {
    const value = e.target.value;
    setForm((s) => ({ ...s, next: value }));
    setPasswordRules(
      evaluatePasswordRules(value, {
        username: user?.username,
        phone: user?.phoneNumber,
      }),
    );
    setError("");

    setPwdOtp("");
    setPwdOtpStatusMsg("");
    setPwdOtpExpiresAtMs(0);
    setPwdOtpResendAvailableAtMs(0);
    setPwdOtpVerified(false);
  };

  const strength = useMemo(
    () =>
      getPasswordStrength(form.next, passwordRules, {
        username: user?.username,
      }),
    [form.next, passwordRules, user?.username],
  );

  let strengthColor = "grey.400";
  let strengthWidth = "0%";
  if (strength.level === "weak") {
    strengthColor = "error.main";
    strengthWidth = "33%";
  } else if (strength.level === "medium") {
    strengthColor = "warning.main";
    strengthWidth = "66%";
  } else if (strength.level === "strong") {
    strengthColor = "success.main";
    strengthWidth = "100%";
  }

  const pwdOtpExpired =
    !pwdOtpVerified && pwdOtpExpiresAtMs > 0 && nowTick >= pwdOtpExpiresAtMs;

  const pwdOtpRemainingSec =
    !pwdOtpVerified && pwdOtpExpiresAtMs
      ? Math.max(0, Math.ceil((pwdOtpExpiresAtMs - nowTick) / 1000))
      : 0;

  const pwdResendCooldownSec =
    !pwdOtpVerified && pwdOtpResendAvailableAtMs
      ? Math.max(0, Math.ceil((pwdOtpResendAvailableAtMs - nowTick) / 1000))
      : 0;

  const userEmail = String(user?.email || "").trim();

  const canSendPwdOtp =
    !pwdOtpVerified && !pwdOtpSending && pwdResendCooldownSec <= 0;
  const canVerifyPwdOtp = !pwdOtpVerified && !!pwdOtp && !pwdOtpVerifying;

  const handleSendPwdOtp = async () => {
    if (!canSendPwdOtp) return;

    setError("");
    setPwdOtpStatusMsg("");
    setPwdOtpSending(true);

    try {
      const payload = await requestEmailOtp({
        purpose: "change_password",
        email: userEmail || "",
        lang,
      });

      const expiresIn = Number(payload?.expiresIn);
      const ttlSec =
        Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 300;

      const now = Date.now();
      setPwdOtpExpiresAtMs(now + ttlSec * 1000);
      setPwdOtpResendAvailableAtMs(now + 60 * 1000);
      setPwdOtpStatusMsg(t("otpSent"));
      setPwdOtpVerified(false);
    } catch (e) {
      const msg = extractWpErrorMessage(e);
      setError(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "passwordFailed",
        }),
      );
    } finally {
      setPwdOtpSending(false);
    }
  };

  const handleVerifyPwdOtp = async () => {
    if (!pwdOtp.trim()) return setError(t("otpRequired"));

    setError("");
    setPwdOtpStatusMsg("");
    setPwdOtpVerifying(true);

    try {
      await verifyEmailOtp({
        purpose: "change_password",
        email: userEmail || "",
        code: String(pwdOtp || "").trim(),
      });

      setPwdOtpStatusMsg(t("otpVerified"));
      setPwdOtpVerified(true);

      setPwdOtpExpiresAtMs(0);
      setPwdOtpResendAvailableAtMs(0);
    } catch (e) {
      const msg = extractWpErrorMessage(e);
      setError(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "passwordFailed",
        }),
      );
    } finally {
      setPwdOtpVerifying(false);
    }
  };

  const actuallySubmit = async () => {
    setSaving(true);
    try {
      setError("");

      const rulesNow = evaluatePasswordRules(form.next, {
        username: user?.username,
        phone: user?.phoneNumber,
      });
      const allOk = Object.values(rulesNow).every(Boolean);
      if (!allOk) return setError(t("passwordRulesNotMet"));

      if (!pwdOtp.trim()) return setError(t("otpRequired"));
      if (!pwdOtpVerified) return setError(t("otpNeedVerifyBeforeSubmitPwd"));
      if (pwdOtpExpired) return setError(t("otpExpired"));

      const data = await gqlChangeMyPassword({
        oldPassword: form.old,
        newPassword: form.next,
        emailOtp: String(pwdOtp || "").trim(),
      });

      const payload = data?.sehoChangeMyPassword;
      if (!payload?.ok) {
        const mapped = mapWpErrorToUiMessage({
          rawMsg: payload?.message || "",
          lang,
          t,
          fallbackKey: "passwordFailed",
        });
        return setError(mapped || payload?.message || t("passwordFailed"));
      }

      setForm({ old: "", next: "" });
      setPasswordRules(initialPasswordRules);

      setPwdOtp("");
      setPwdOtpStatusMsg("");
      setPwdOtpExpiresAtMs(0);
      setPwdOtpResendAvailableAtMs(0);
      setPwdOtpVerified(false);

      setOk(true);
    } catch (e) {
      console.error(e);
      const msg = extractWpErrorMessage(e);
      setError(
        mapWpErrorToUiMessage({
          rawMsg: msg,
          lang,
          t,
          fallbackKey: "passwordFailed",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const rulesNow = evaluatePasswordRules(form.next, {
      username: user?.username,
      phone: user?.phoneNumber,
    });
    setPasswordRules(rulesNow);

    const allOk = Object.values(rulesNow).every(Boolean);
    if (!allOk) return setError(t("passwordRulesNotMet"));

    const currentStrength = getPasswordStrength(form.next, rulesNow, {
      username: user?.username,
    });
    if (currentStrength.level === "weak") return setConfirmWeakOpen(true);

    await actuallySubmit();
  };

  const langCode = normalizeLang(lang);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontFamily: fonts[lang], fontWeight: 900, mb: 2 }}
      >
        {t("passwordTitle")}
      </Typography>

      <Stack spacing={2}>
        <TextField
          fullWidth
          type={showOld ? "text" : "password"}
          label={t("passwordOldLabel")}
          placeholder={t("passwordOldPlaceholder")}
          value={form.old}
          onChange={change("old")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOld(!showOld)} edge="end">
                  {showOld ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showNext ? "text" : "password"}
          label={t("passwordNewLabel")}
          placeholder={t("passwordNewPlaceholder")}
          helperText={t("passwordHint")}
          value={form.next}
          onChange={handleNextChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNext(!showNext)} edge="end">
                  {showNext ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {!!form.next && (
          <Box sx={{ mt: -1 }}>
            <Box
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: "grey.200",
                overflow: "hidden",
                mb: 0.75,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: strengthWidth,
                  transition: "width 0.25s ease",
                  bgcolor: strengthColor,
                }}
              />
            </Box>

            {PASSWORD_RULE_ITEMS.map((item) => {
              const passed = passwordRules[item.key];
              const Icon = passed ? CheckCircleRoundedIcon : CancelRoundedIcon;
              return (
                <Stack
                  key={item.key}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.25 }}
                >
                  <Icon
                    sx={{
                      fontSize: 18,
                      color: passed ? "success.main" : "error.main",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: passed ? "success.main" : "error.main",
                      fontSize: 12,
                      fontFamily: fonts[lang],
                    }}
                  >
                    {t(item.labelKey)}
                  </Typography>
                </Stack>
              );
            })}
          </Box>
        )}

        <Divider sx={{ my: 0.5 }} />

        <Typography
          sx={{ fontFamily: fonts[lang], fontWeight: 800, fontSize: 13 }}
        >
          {langCode === "ZH"
            ? "安全驗證（Email 驗證碼）"
            : langCode === "JP"
              ? "セキュリティ確認（メール認証）"
              : "Security verification (Email OTP)"}
        </Typography>

        <TextField
          fullWidth
          label={t("otpLabel")}
          value={pwdOtp}
          onChange={(e) => setPwdOtp(sanitizeOtpInput(e.target.value))}
          disabled={pwdOtpVerified}
          helperText={
            pwdOtpStatusMsg
              ? pwdOtpStatusMsg
              : pwdOtpExpired
                ? t("otpExpired")
                : langCode === "ZH"
                  ? "修改密碼前需要 Email 驗證。請輸入寄到您的信箱的 6 位數驗證碼。"
                  : langCode === "JP"
                    ? "パスワード変更にはメール認証が必要です。届いた 6 桁コードを入力してください。"
                    : "Changing password requires email verification. Enter the 6-digit code sent to your email."
          }
          FormHelperTextProps={{ sx: { mt: 0.5 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MarkEmailReadRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {!!pwdOtpExpiresAtMs && !pwdOtpExpired && !pwdOtpVerified && (
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {formatMMSS(pwdOtpRemainingSec)}
                  </Typography>
                )}
                {pwdOtpVerified && (
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: "success.main" }}
                  />
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": pwdOtpVerified
              ? {
                  "& fieldset": { borderColor: "success.main" },
                  "&:hover fieldset": { borderColor: "success.main" },
                  "&.Mui-focused fieldset": { borderColor: "success.main" },
                }
              : {},
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            type="button"
            variant="outlined"
            onClick={handleSendPwdOtp}
            disabled={!canSendPwdOtp}
            startIcon={
              pwdOtpExpiresAtMs ? <ReplayRoundedIcon /> : <SendRoundedIcon />
            }
            sx={{ whiteSpace: "nowrap" }}
          >
            {pwdOtpExpiresAtMs ? t("otpResend") : t("otpSend")}
            {pwdResendCooldownSec > 0 ? ` (${pwdResendCooldownSec}s)` : ""}
          </Button>

          <Button
            type="button"
            variant="text"
            onClick={handleVerifyPwdOtp}
            disabled={!canVerifyPwdOtp}
            sx={{ whiteSpace: "nowrap" }}
          >
            {pwdOtpVerifying
              ? "..."
              : langCode === "ZH"
                ? "驗證"
                : langCode === "JP"
                  ? "確認"
                  : "Verify"}
          </Button>
        </Stack>

        {!pwdOtpVerified && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {t("otpNeedVerifyBeforeSubmitPwd")}
          </Typography>
        )}

        <AuthErrorMessage text={error} />

        <Stack alignItems="center" sx={{ pt: 2 }}>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || !pwdOtpVerified || pwdOtpSending}
          >
            {t("passwordConfirmButton")}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={confirmWeakOpen} onClose={() => setConfirmWeakOpen(false)}>
        <DialogTitle>
          {langCode === "ZH"
            ? "密碼強度偏弱"
            : langCode === "JP"
              ? "パスワード強度が弱いです"
              : "Password strength is weak"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {langCode === "ZH"
              ? "雖然已符合所有規則，但系統判定此密碼強度偏弱。確定要使用這組密碼嗎？"
              : langCode === "JP"
                ? "すべての条件は満たしていますが、このパスワードは弱いと判定されています。このまま使用してもよろしいですか？"
                : "All rules are satisfied, but this password is still considered weak. Do you want to use it anyway?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWeakOpen(false)}>
            {langCode === "ZH"
              ? "取消"
              : langCode === "JP"
                ? "キャンセル"
                : "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmWeakOpen(false);
              await actuallySubmit();
            }}
            disabled={saving || !pwdOtpVerified}
          >
            {langCode === "ZH"
              ? "仍要使用"
              : langCode === "JP"
                ? "このまま使う"
                : "Use anyway"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={ok} autoHideDuration={2000} onClose={() => setOk(false)}>
        <Alert severity="success" variant="filled">
          {t("passwordUpdatedMessage")}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default function BasicTab({ user, lang, onUserUpdated }) {
  return (
    <>
      <BasicInfoForm user={user} lang={lang} onUserUpdated={onUserUpdated} />
      <PasswordForm lang={lang} user={user} />
    </>
  );
}
